// src/services/authService.ts
import { api } from './api';
import secureLocalStorage from "react-secure-storage";
import { tokenService } from './tokenService';
import { clearStoredSession } from './sessionService';
import { STORAGE_KEYS } from '../constants';

export interface Permission {
  _id: string;
  name: string;
  slug: string;
  group: string; // Add this if not already present
}

export interface Role {
  _id: string;
  name: string;
  slug: string;
  description: string;
  updatedAt: string;
  permissions: Permission[];
}

export interface User {
  _id: string;
  name: string;
  email: string;
  role: Role;
  status: string;
  department: string;
  isClient: boolean;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface ResetPasswordRequest {
  email: string;
  frontend_url: string;
}

interface ChangePasswordRequest {
  email: string;
  newPassword: string;
  confirmPassword: string;
}

interface UpdatePasswordRequest {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface AuthResponse {
  message: string;
  data: {
    user: User;
    accesstoken: string;
    refreshtoken: string;
  };
  error: boolean;
}

interface ResetPasswordResponse {
  message: string;
  error: boolean;
}

interface UpdatePasswordResponse {
  message: string;
  error: boolean;
}

interface RolesResponse {
  message: string;
  data: Role[];
  error: boolean;
}

interface PermissionsResponse {
  message: string;
  data: Permission[];
  error: boolean;
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<User> {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    if (response.data.data) {
      const { accesstoken, refreshtoken, user } = response.data.data;
      // Store tokens and user data securely
      secureLocalStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accesstoken);
      secureLocalStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshtoken);
      secureLocalStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      
      // Start monitoring for token refreshes
      tokenService.startTokenRefreshMonitor();
      
      return user;
    }
    throw new Error('Login failed');
  },

  async requestPasswordReset(resetData: ResetPasswordRequest): Promise<string> {
    const response = await api.post<ResetPasswordResponse>('/auth/reset-password', resetData);
    
    if (!response.data.error) {
      return response.data.message;
    }
    throw new Error(response.data.message || 'Password reset request failed');
  },

  async changePassword(changeData: ChangePasswordRequest, token: string): Promise<string> {
    const response = await api.post<ResetPasswordResponse>(
      '/auth/change-password', 
      changeData,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    
    if (!response.data.error) {
      return response.data.message;
    }
    throw new Error(response.data.message || 'Password change failed');
  },

  async updatePassword(updateData: UpdatePasswordRequest): Promise<string> {
    const token = this.getAccessToken();
    const response = await api.post<UpdatePasswordResponse>(
      '/auth/change-password', 
      updateData,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    
    if (!response.data.error) {
      return response.data.message;
    }
    throw new Error(response.data.message || 'Password update failed');
  },

  async getRoles(): Promise<Role[]> {
    const response = await api.get<RolesResponse>('/role');
    return response.data.data;
  },

  async getAllPermissions(): Promise<Permission[]> {
    const response = await api.get<PermissionsResponse>('/role/all/permissions');
    return response.data.data;
  },

  logout(): void {
    tokenService.stopTokenRefreshMonitor();
    clearStoredSession();
  },

  getCurrentUser(): User | null {
    const userStr = secureLocalStorage.getItem(STORAGE_KEYS.USER);
    return userStr ? JSON.parse(userStr as string) : null;
  },

  isAuthenticated(): boolean {
    return !!secureLocalStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  },

  hasPermission(permissionSlug: string): boolean {
    const user = this.getCurrentUser();
    if (!user || !user.role || !user.role.permissions) return false;
    
    return user.role.permissions.some((permission: Permission) => {
      return permission.slug === permissionSlug;
    });
  },

  hasAnyPermission(permissionSlugs: string[]): boolean {
    const user = this.getCurrentUser();
    if (!user || !user.role || !user.role.permissions) return false;
    
    return user.role.permissions.some((permission: Permission) => {
      return permissionSlugs.includes(permission.slug);
    });
  },

  getAccessToken(): string | null {
    return secureLocalStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) as string | null;
  },

  getRefreshToken(): string | null {
    return secureLocalStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) as string | null;
  },
  
  // Method to manually trigger token refresh
  async refreshAuthTokens(): Promise<boolean> {
    try {
      await tokenService.refreshToken();
      return true;
    } catch (error) {
      console.error('Manual token refresh failed:', error);
      return false;
    }
  }
};
